// frontend/hooks/usePermissions.js
import { useMemo } from 'react';
import { 
    PERMISSION_LEVELS as PERMISSION_CONSTANTS,
    ROLE_DEFINITIONS,
    ROLE_NAMES,
    ROLE_IDS,
    ROLE_DISPLAY_NAME_TO_KEY,
    ROLE_ID_TO_KEY,
    normalizeRole, // Import the new function
    canView,
    canEdit,
    canEditTicket,
    hasProductAccess,
    isGodMode,
    canGenerateReport,
    isAssigneeDisabled
} from '../const/roles';

export const PERMISSION_LEVELS = PERMISSION_CONSTANTS;

// Remove the old normalizeRole function since it's now imported

/**
 * Custom React hook that wraps backend permission functions
 * @param {object} user - The user object
 * @returns {object} Memoized permission check functions
 */
export const usePermissions = (user) => {
    // Memoize normalized user so it only recomputes if user changes
    const normalizedUser = useMemo(() => {
        if (!user) {
            console.log('usePermissions: No user provided');
            return null;
        }
        
        console.log('usePermissions: Raw user role:', user.role, typeof user.role);
        const normalizedRole = normalizeRole(user.role); // Use imported function
        
        if (!normalizedRole) {
            console.log('usePermissions: Failed to normalize role:', user.role);
            return null;
        }
        
        const normalizedUserObj = { ...user, role: normalizedRole };
        console.log('usePermissions: Normalized user:', normalizedUserObj);
        return normalizedUserObj;
    }, [user]);

    return useMemo(() => {
        // Safe defaults if user not ready yet
        if (!normalizedUser) {
            console.log('usePermissions: No normalized user, returning defaults');
            return {
                canView: () => false,
                canEdit: () => false,
                canEditTicket: () => false,
                canViewTicket: () => false,
                hasProductAccess: () => false,
                isGodMode: false,
                canGenerateReport: false,
                isAssigneeDisabled: true,
                PERMISSION_LEVELS
            };
        }

        // Normal flow when user is available
        const permissionsObj = {
            // Core permission checks
            canView: (resourceType, requiredLevel) => canView(normalizedUser, resourceType, requiredLevel),
            canEdit: (resourceType, requiredLevel) => canEdit(normalizedUser, resourceType, requiredLevel),
            
            // Ticket permissions
            canEditTicket: (ticketData) => canEditTicket(normalizedUser, ticketData),
            canViewTicket: () => true,
            
            // Product permissions
            hasProductAccess: (productId, contractId) => hasProductAccess(normalizedUser, productId, contractId),
            
            // Special permission flags
            isGodMode: isGodMode(normalizedUser),
            canGenerateReport: (resourceType = 'reports', requiredLevel = PERMISSION_LEVELS.ALL) => 
            isGodMode(normalizedUser) || canView(normalizedUser, resourceType, requiredLevel),
            isAssigneeDisabled: isAssigneeDisabled(normalizedUser),
            
            // Permission level constants
            PERMISSION_LEVELS
        };

        console.log('usePermissions: Created permissions object for user:', {
            user: normalizedUser,
            permissions: {
                isGodMode: permissionsObj.isGodMode,
                canEditTicketsAll: permissionsObj.canEdit('tickets', PERMISSION_LEVELS.ALL),
                canEditTicketsAssigned: permissionsObj.canEdit('tickets', PERMISSION_LEVELS.ASSIGNED)
            }
        });

        return permissionsObj;
    }, [normalizedUser]);
};